import { useCallback, useEffect, useMemo } from 'react';

import { COOKIE_KEYS } from '@moniebook-web/types';
import { getDeviceInfo, getItem, useUserInfo } from '@moniebook-web/utils';
import { type ChatSDKInitOptions, chatSDKLauncher } from '@moniedesk-chat/sdk';

import { config } from '@/config';

const IDENTIFIER_TYPE = 'USER_ID';

export function useChat() {
    const { user } = useUserInfo();

    const token = getItem(COOKIE_KEYS.TOKEN_VARIABLE);

    const updatedPayload = useMemo<ChatSDKInitOptions>(() => {
        return {
            widgetHost: config.MONIEDESK.widgetHost,
            customerName: user
                ? `${user.firstName ?? ''} ${user.lastName ?? ''}`
                : undefined,
            customerEmail: user?.email,
            customerType: 'BUSINESS',
            customerPhoneNumber: user ? '' : undefined,
            host: globalThis.location.origin,
            entityId: config.MONIEDESK.entityId,
            source: 'WEBCHAT',
            defaultOpen: false,
            pageUrl: globalThis.location.href,
            token: token ? `bearer_${token}` : undefined,
            width: '420' as any,
            height: 80,
            platformType: 'WEB',
            identifier: user ? user?.userId : undefined,
            identifierType: user ? IDENTIFIER_TYPE : undefined,
            canShowBubbleBaseOnClose: true,
            deviceId: getDeviceInfo().deviceUniqueIdentifier.toString(),
        };
    }, [token, user]);

    const initializeChat = useCallback(() => {
        if (!chatSDKLauncher.isLoaded()) {
            chatSDKLauncher.init(updatedPayload);
            chatSDKLauncher.updateEngagement(updatedPayload);
        }
    }, [updatedPayload]);

    const unloadChat = useCallback(() => {
        if (chatSDKLauncher.isLoaded()) {
            chatSDKLauncher.unload();
        }
    }, []);

    useEffect(() => {
        const entityId = config.MONIEDESK.entityId;

        if (!entityId) return;

        unloadChat();
        initializeChat();
    }, [initializeChat, unloadChat]);

    return {
        unloadChat,
    };
}
